import { useMemo, useState } from "react";
import type { ReactNode, JSX} from "react";
import { AppShell, LeftRail } from "../components/layout";
import { Icon } from "../components/ui";
import type { ModuleIcon } from "../components/layout/LeftRail";
import { useRouterContext } from "../providers/RouterProvider";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import type { ModuleDefinition } from "../runtime/contracts";
import { PrivateWorkspaceModal } from "../components/auth/PrivateWorkspaceModal";
import { WorkspaceMenuTrigger } from "../components/auth/WorkspaceMenuTrigger";
import { getStoredUserForWorkspace, useAuthStore } from "../store/authStore";
import {
  LOCAL_WORKSPACE_ID,
  MANAGED_CLOUD_WORKSPACE_ID,
  getWorkspaceById,
  useWorkspaceStore,
} from "../store/workspaceStore";
import { isRailModuleVisible, orderRailModules } from "./moduleOrder";
import {
  useExtensionsList,
  useSetExtensionPosition,
} from "../modules/extensions/queries";

const BOTTOM_MODULE_IDS = new Set(["settings"]);

/** Catalog module-extension info derived from `extensions.list`. Holds ALL
 * catalog module entries (installed or not) so the rail can both order them and
 * hide the ones that are not installed+enabled. */
interface ExtensionRailInfo {
  /** Canonical wire key, e.g. `module:magnis.contacts` — used by set_position. */
  readonly key: string;
  readonly position: number;
  readonly installed: boolean;
  readonly enabled: boolean;
}

function buildLeftRailIcons(
  modules: readonly ModuleDefinition[],
  activeModuleId: string,
  setActiveModule: (id: string) => void,
  extensionsByModule: ReadonlyMap<string, ExtensionRailInfo>,
): { top: ModuleIcon[]; bottom: ModuleIcon[] } {
  const positions = new Map<string, number>();
  for (const [id, info] of extensionsByModule) {
    positions.set(id, info.position);
  }
  const withIcons = orderRailModules(
    modules
      .filter((m) => m.icon !== undefined)
      // A module that is a CATALOG extension shows in the rail only while it is
      // installed AND enabled; disabling/uninstalling it removes the tab.
      // Core modules (inbox/episodes/settings/extensions — not in the catalog)
      // are always shown.
      .filter((m) => isRailModuleVisible(m.id, extensionsByModule))
      .map((m) => ({
        id: m.id,
        icon: m.icon ?? <Icon name="bot" size={20} />,
        label: m.title,
        active: activeModuleId === m.id,
        // Extension icons (those backed by an installed extension row) are
        // user-reorderable (DEC-17); pinned Core entries are not.
        reorderable: extensionsByModule.has(m.id),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => { setActiveModule(m.id); },
      })),
    positions,
  );

  return {
    top: withIcons.filter((m) => !BOTTOM_MODULE_IDS.has(m.id)),
    bottom: withIcons.filter((m) => BOTTOM_MODULE_IDS.has(m.id)),
  };
}

interface AppLayoutProps {
  readonly fallback: ReactNode;
}

export function AppLayout({
  fallback,
}: AppLayoutProps): JSX.Element {
  const { activeModuleId, setActiveModule } = useRouterContext();
  const runtime = useAppRuntime();
  const [privateModalOpen, setPrivateModalOpen] = useState(false);
  const selectWorkspace = useWorkspaceStore((state) => state.selectWorkspace);
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const currentUserEmail = useAuthStore((state) => state.user?.email ?? null);
  const logout = useAuthStore((state) => state.logout);

  // DEC-17: the rail orders extension icons by their DB-persisted position.
  const { data: extensionsData } = useExtensionsList({ kind: "module" });
  const setExtensionPosition = useSetExtensionPosition();
  const extensionsByModule = useMemo(() => {
    const map = new Map<string, ExtensionRailInfo>();
    for (const ext of extensionsData?.extensions ?? []) {
      // Keep ALL catalog module entries (installed or not) so the rail knows
      // which ids are catalog extensions (to gate visibility) vs Core.
      // Canonical id `module:<producer>.<name>` → rail module id = <name>.
      const name = ext.id.split(".").pop();
      if (name !== undefined && name.length > 0) {
        map.set(name, {
          key: ext.id,
          position: ext.position,
          installed: ext.installed,
          enabled: ext.enabled,
        });
      }
    }
    return map;
  }, [extensionsData]);

  const { top, bottom } = buildLeftRailIcons(
    runtime.modules.list(),
    activeModuleId,
    setActiveModule,
    extensionsByModule,
  );

  // Drop of extension icon A onto extension icon B: recompute the extension
  // segment order and persist sequential positions (extensions.set_position).
  const handleRailReorder = (
    dragId: string,
    overId: string,
    position: "before" | "after",
  ): void => {
    const segment = top
      .filter((m) => extensionsByModule.has(m.id))
      .map((m) => m.id);
    const from = segment.indexOf(dragId);
    if (from < 0 || !segment.includes(overId) || dragId === overId) return;
    segment.splice(from, 1);
    // Recompute the target index AFTER removal, then insert before/after it so
    // the drop lands exactly where the indicator line showed.
    const overIdx = segment.indexOf(overId);
    const to = position === "after" ? overIdx + 1 : overIdx;
    segment.splice(to, 0, dragId);
    segment.forEach((id, idx) => {
      const info = extensionsByModule.get(id);
      if (info !== undefined && info.position !== idx + 1) {
        setExtensionPosition.mutate({ key: info.key, position: idx + 1 });
      }
    });
  };

  const activeModule = runtime.modules.get(activeModuleId);
  const cloudUser = getStoredUserForWorkspace(MANAGED_CLOUD_WORKSPACE_ID);
  const privateWorkspaceInitialEmail =
    selectedWorkspaceId === MANAGED_CLOUD_WORKSPACE_ID
      ? currentUserEmail ?? cloudUser?.email ?? null
      : cloudUser?.email ?? null;
  const localWorkspace = getWorkspaceById(LOCAL_WORKSPACE_ID);
  const workspaceMenuItems = useMemo(() => [
    {
      id: LOCAL_WORKSPACE_ID,
      label: localWorkspace?.label ?? "Local Workspace",
      selected: selectedWorkspaceId === LOCAL_WORKSPACE_ID,
      onSelect: (): void => {
        void selectWorkspace(LOCAL_WORKSPACE_ID);
      },
    },
    {
      id: MANAGED_CLOUD_WORKSPACE_ID,
      label: cloudUser?.email
        ? `${cloudUser.email} (Magnis Cloud)`
        : "Log in to Magnis AI",
      selected: selectedWorkspaceId === MANAGED_CLOUD_WORKSPACE_ID,
      onSelect: (): void => {
        void selectWorkspace(MANAGED_CLOUD_WORKSPACE_ID);
      },
    },
  ], [cloudUser?.email, localWorkspace?.label, selectWorkspace, selectedWorkspaceId]);

  return (
    <>
      <AppShell
        leftRail={
          <LeftRail
            modules={top}
            bottomModules={bottom}
            onReorder={handleRailReorder}
            footer={
              <WorkspaceMenuTrigger
                items={workspaceMenuItems}
                activeCloudSession={
                  selectedWorkspaceId === MANAGED_CLOUD_WORKSPACE_ID &&
                  currentUserEmail != null
                    ? { email: currentUserEmail, onLogout: logout }
                    : null
                }
                onRequestPrivateWorkspace={() => {
                  setPrivateModalOpen(true);
                }}
              />
            }
          />
        }
      >
        {activeModule?.Component ? (
          <activeModule.Component key={activeModule.id} />
        ) : (
          fallback
        )}
      </AppShell>
      {privateModalOpen ? (
        <PrivateWorkspaceModal
          initialEmail={privateWorkspaceInitialEmail}
          onClose={() => { setPrivateModalOpen(false); }}
        />
      ) : null}
    </>
  );
}
